import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"
import { insertCatalogEntry } from "../catalog_insert"

// Drives the Leistungsverzeichnis position tree: group collapse/expand with
// sessionStorage persistence, per-parent drag & drop reordering, and the
// client-side search/filter/sort control bar. Filter and view-only sort state
// also live in sessionStorage so they survive the full #boq_tree turbo-stream
// replace that every item mutation performs. While a filter or a non-standard
// sort is active, drag & drop is disabled — the reorder PATCH reads ALL
// container children, so dragging would persist an order interleaved with
// hidden or visually re-sorted siblings.
export default class extends Controller {
  static targets = [
    "children",
    "subtree",
    "quickAdd",
    "searchInput",
    "typeSelect",
    "unitSelect",
    "incompleteToggle",
    "sortSelect",
    "sortDirButton",
    "counter",
    "resetLink",
    "dragHint",
  ]

  static values = {
    documentId: Number,
    reorderUrl: String,
    catalogInsertUrl: String,
    catalogInsertError: String,
    editable: Boolean,
  }

  connect() {
    this.collapsed = new Set(JSON.parse(sessionStorage.getItem(this.storageKey) || "[]"))
    this.filterState = this.loadFilterState()
    this.captureOriginalOrder()
    this.restoreControlValues()
    this.applyVisibility()
    if (this.editableValue) this.initSortables()
    this.syncDragState()
    this.reopenQuickAddForm()
  }

  // Continuous keyboard entry: after a quick-add form was submitted, the whole
  // tree re-renders; reopen the same form (focused) so the user can keep typing.
  reopenQuickAddForm() {
    const reopen = window.__boqReopen
    window.__boqReopen = null
    if (!reopen || Date.now() - reopen.at > 5000) return

    const link = this.element.querySelector(`a[href="${reopen.href}"]`)
    if (link) link.click()
  }

  // --- Collapse / expand ---

  toggle(event) {
    const groupId = String(event.params.id)
    if (this.collapsed.has(groupId)) {
      this.collapsed.delete(groupId)
    } else {
      this.collapsed.add(groupId)
    }
    this.persistCollapsedState()
    this.applyVisibility()
  }

  expandAll() {
    this.collapsed.clear()
    this.persistCollapsedState()
    this.applyVisibility()
  }

  collapseAll() {
    this.subtreeTargets.forEach((subtree) => this.collapsed.add(String(subtree.dataset.groupId)))
    this.persistCollapsedState()
    this.applyVisibility()
  }

  persistCollapsedState() {
    sessionStorage.setItem(this.storageKey, JSON.stringify([...this.collapsed]))
  }

  // --- Filter / sort state ---

  defaultFilterState() {
    return { query: "", type: "", unit: "", incomplete: false, sortKey: "standard", sortDir: "asc" }
  }

  loadFilterState() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.filterStorageKey) || "{}")
      return { ...this.defaultFilterState(), ...stored }
    } catch {
      return this.defaultFilterState()
    }
  }

  persistFilterState() {
    sessionStorage.setItem(this.filterStorageKey, JSON.stringify(this.filterState))
  }

  restoreControlValues() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = this.filterState.query
    if (this.hasTypeSelectTarget) this.typeSelectTarget.value = this.filterState.type
    if (this.hasUnitSelectTarget) this.unitSelectTarget.value = this.filterState.unit
    if (this.hasIncompleteToggleTarget)
      this.incompleteToggleTarget.checked = this.filterState.incomplete
    if (this.hasSortSelectTarget) this.sortSelectTarget.value = this.filterState.sortKey
    this.updateSortDirIcon()
  }

  filterChanged() {
    clearTimeout(this.filterDebounce)
    this.filterDebounce = setTimeout(() => this.commitFilterChange(), 150)
  }

  commitFilterChange() {
    this.filterState = {
      query: (this.hasSearchInputTarget ? this.searchInputTarget.value : "").trim().toLowerCase(),
      type: this.hasTypeSelectTarget ? this.typeSelectTarget.value : "",
      unit: this.hasUnitSelectTarget ? this.unitSelectTarget.value : "",
      incomplete: this.hasIncompleteToggleTarget && this.incompleteToggleTarget.checked,
      sortKey: this.hasSortSelectTarget ? this.sortSelectTarget.value : "standard",
      sortDir: this.filterState.sortDir,
    }
    this.persistFilterState()
    this.applyVisibility()
    this.syncDragState()
  }

  toggleSortDir() {
    this.filterState.sortDir = this.filterState.sortDir === "asc" ? "desc" : "asc"
    this.persistFilterState()
    this.updateSortDirIcon()
    this.applyVisibility()
  }

  updateSortDirIcon() {
    if (!this.hasSortDirButtonTarget) return
    const icon = this.sortDirButtonTarget.querySelector("[data-role='sort-dir-icon']")
    if (icon) icon.classList.toggle("rotate-180", this.filterState.sortDir === "desc")
  }

  resetFilters() {
    this.filterState = this.defaultFilterState()
    this.persistFilterState()
    this.restoreControlValues()
    this.applyVisibility()
    this.syncDragState()
  }

  filterActive() {
    const state = this.filterState
    return state.query !== "" || state.type !== "" || state.unit !== "" || state.incomplete
  }

  sortActive() {
    return this.filterState.sortKey !== "standard"
  }

  // --- Visibility pipeline: sort, then collapse, then filter on top ---

  applyVisibility() {
    this.applySort()
    this.applyCollapsedState()
    this.applyFilter()
  }

  applyCollapsedState() {
    this.subtreeTargets.forEach((subtree) => {
      const isCollapsed = this.collapsed.has(String(subtree.dataset.groupId))
      subtree.classList.toggle("hidden", isCollapsed)
      const chevron = this.element.querySelector(
        `[data-boq-tree-chevron="${subtree.dataset.groupId}"]`,
      )
      if (chevron) chevron.classList.toggle("-rotate-90", isCollapsed)
    })
  }

  applyFilter() {
    const active = this.filterActive()
    this.quickAddTargets.forEach((el) => el.classList.toggle("hidden", active))
    if (this.hasResetLinkTarget) {
      this.resetLinkTarget.classList.toggle("hidden", !(active || this.sortActive()))
    }

    if (!active) {
      this.element
        .querySelectorAll("div[data-kind]")
        .forEach((node) => node.classList.remove("hidden"))
      if (this.hasCounterTarget) this.counterTarget.classList.add("hidden")
      return
    }

    let matchCount = 0
    this.rootNodes().forEach((node) => {
      matchCount += this.processNode(node, false)
    })
    this.updateCounter(matchCount)
  }

  // Marks a node (and its subtree) visible when it matches, contains a match,
  // or sits under a matching group. Returns the number of visible positions.
  processNode(node, ancestorMatched) {
    const self = this.matchesSelf(node)
    const subtree = node.querySelector(":scope > [data-boq-tree-target='subtree']")
    let descendantCount = 0
    let descendantMatched = false

    if (subtree) {
      this.childNodesOf(subtree).forEach((child) => {
        const count = this.processNode(child, ancestorMatched || self)
        descendantCount += count
        if (count > 0 || !child.classList.contains("hidden")) descendantMatched = true
      })
    }

    const visible = self || descendantMatched || ancestorMatched
    node.classList.toggle("hidden", !visible)
    if (subtree && visible) subtree.classList.remove("hidden")

    let count = descendantCount
    if (visible && node.dataset.kind === "position") count += 1
    return count
  }

  matchesSelf(node) {
    const data = node.dataset
    const state = this.filterState
    if (state.query && !(data.search || "").includes(state.query)) return false
    if (state.type && data.positionType !== state.type) return false
    if (state.unit && data.unit !== state.unit) return false
    if (state.incomplete && data.incomplete !== "1") return false
    return true
  }

  rootNodes() {
    const root = this.childrenTargets.find((container) => !container.dataset.parentId)
    return root ? this.nodeChildren(root) : []
  }

  childNodesOf(subtree) {
    const container = subtree.querySelector(":scope > .boq-children")
    return container ? this.nodeChildren(container) : []
  }

  nodeChildren(container) {
    return Array.from(container.children).filter((el) => el.dataset && el.dataset.kind)
  }

  updateCounter(matchCount) {
    if (!this.hasCounterTarget) return
    const template =
      matchCount === 1
        ? this.counterTarget.dataset.matchesOne
        : this.counterTarget.dataset.matchesOther
    this.counterTarget.textContent = (template || "").replace("%{count}", matchCount)
    this.counterTarget.classList.remove("hidden")
  }

  // --- View-only sorting (never persisted) ---

  captureOriginalOrder() {
    this.originalOrder = new Map()
    this.childrenTargets.forEach((container) => {
      this.originalOrder.set(container, this.nodeChildren(container))
    })
  }

  applySort() {
    if (!this.sortActive()) {
      this.restoreOriginalOrder()
      return
    }

    const direction = this.filterState.sortDir === "desc" ? -1 : 1
    this.childrenTargets.forEach((container) => {
      this.nodeChildren(container)
        .map((node, index) => ({ node, index }))
        .sort((a, b) => this.compareNodes(a, b, this.filterState.sortKey, direction))
        .forEach((entry) => container.appendChild(entry.node))
    })
  }

  restoreOriginalOrder() {
    if (!this.originalOrder) return
    this.originalOrder.forEach((nodes, container) => {
      nodes.forEach((node) => container.appendChild(node))
    })
  }

  compareNodes(a, b, key, direction) {
    if (key === "text") {
      const cmp = (a.node.dataset.sortText || "").localeCompare(b.node.dataset.sortText || "", "de")
      return cmp !== 0 ? cmp * direction : a.index - b.index
    }

    const attr = { quantity: "sortQuantity", unit_price: "sortUnitPrice", total: "sortTotal" }[key]
    const aValue = parseFloat(a.node.dataset[attr])
    const bValue = parseFloat(b.node.dataset[attr])
    const aMissing = Number.isNaN(aValue)
    const bMissing = Number.isNaN(bValue)
    if (aMissing && bMissing) return a.index - b.index
    if (aMissing) return 1 // missing values always last, regardless of direction
    if (bMissing) return -1
    if (aValue === bValue) return a.index - b.index
    return (aValue - bValue) * direction
  }

  // --- Drag & drop ---

  initSortables() {
    // Unique group name per container keeps internal reordering isolated to one
    // parent (a cross-parent move can't be persisted by the position-only reorder
    // PATCH), while `put: ["boq-catalog"]` lets catalog picker items drop in.
    this.sortables = this.childrenTargets.map(
      (container, index) =>
        new Sortable(container, {
          draggable: "[data-id]",
          handle: "[data-drag-handle]",
          animation: 150,
          ghostClass: "bg-ghost",
          group: { name: `boq-tree-${index}`, pull: false, put: ["boq-catalog"] },
          // Match the picker's pointer-based drag so a clone dragged out of the fixed
          // slide-over is recognised as a drop over these containers.
          forceFallback: true,
          fallbackOnBody: true,
          onEnd: (event) => this.reorder(event),
          onAdd: (event) => this.insertFromCatalog(event),
        }),
    )
  }

  // A catalog baustein was dragged from the picker into a tree container. Insert it
  // at the drop location; the streamed tree re-render replaces the transient clone.
  insertFromCatalog(event) {
    insertCatalogEntry(event, {
      url: this.hasCatalogInsertUrlValue ? this.catalogInsertUrlValue : null,
      errorMessage: this.catalogInsertErrorValue,
    })
  }

  syncDragState() {
    const disabled = this.sortActive() || this.filterActive()
    ;(this.sortables || []).forEach((sortable) => sortable.option("disabled", disabled))
    this.element.classList.toggle("boq-drag-disabled", disabled)
    if (this.hasDragHintTarget) {
      this.dragHintTarget.classList.toggle("hidden", !(disabled && this.editableValue))
    }
  }

  reorder(event) {
    const container = event.to
    const parentId = container.dataset.parentId || null
    const positions = Array.from(container.children)
      .filter((el) => el.dataset.id)
      .map((el, index) => ({ id: parseInt(el.dataset.id), position: index + 1 }))
    if (positions.length === 0) return

    const token = document.head.querySelector('meta[name="csrf-token"]').getAttribute("content")
    fetch(this.reorderUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token,
        Accept: "text/vnd.turbo-stream.html",
        "Content-type": "application/json",
      },
      body: JSON.stringify({ parent_id: parentId, positions: positions }),
    })
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html))
  }

  get storageKey() {
    return `boq-tree-collapsed-${this.documentIdValue}`
  }

  get filterStorageKey() {
    return `boq-tree-filter-${this.documentIdValue}`
  }
}
